import { useState, useMemo, useCallback, useRef } from 'react';
import { METRIC_TIPS } from '../api/format';

export interface Column<T> {
  key: string; label: string;
  render: (row: T) => React.ReactNode;
  sortValue?: (row: T) => string | number | null;
  align?: 'left' | 'right' | 'center';
  width?: string;
  tip?: string;
  resizable?: boolean;
}

interface DataTableProps<T> {
  columns: Column<T>[]; rows: T[]; rowKey: (row: T) => string;
  onRowClick?: (row: T) => void;
  emptyMessage?: string;
  filteredEmpty?: boolean;
  stickyFirstCol?: boolean;
  totalRows?: number;
  selectable?: boolean;
  selectedKeys?: Set<string>;
  onSelectionChange?: (keys: Set<string>) => void;
}

interface SortEntry { key: string; dir: 'asc' | 'desc' }

export function DataTable<T>({
  columns, rows, rowKey, onRowClick, emptyMessage = 'No data',
  filteredEmpty = false, stickyFirstCol = false, totalRows,
  selectable = false, selectedKeys, onSelectionChange,
}: DataTableProps<T>) {
  // Multi-sort: array of sort entries. Shift-click adds, plain click replaces.
  const [sorts, setSorts] = useState<SortEntry[]>([]);
  const [focusedRow, setFocusedRow] = useState(-1);
  const [colWidths, setColWidths] = useState<Record<string, number>>({});
  const resizingRef = useRef<{ key: string; startX: number; startW: number } | null>(null);

  const handleSort = useCallback((key: string, shift: boolean) => {
    setSorts(prev => {
      const existing = prev.findIndex(s => s.key === key);
      if (shift) {
        // Multi-sort: toggle or add
        if (existing >= 0) {
          const updated = [...prev];
          updated[existing] = { key, dir: prev[existing]!.dir === 'desc' ? 'asc' : 'desc' };
          return updated;
        }
        return [...prev, { key, dir: 'desc' }];
      }
      // Single sort: replace
      if (existing >= 0 && prev.length === 1) {
        return [{ key, dir: prev[0]!.dir === 'desc' ? 'asc' : 'desc' }];
      }
      return [{ key, dir: 'desc' }];
    });
  }, []);

  const sortedRows = useMemo(() => {
    if (sorts.length === 0) return rows;
    return [...rows].sort((a, b) => {
      for (const { key, dir } of sorts) {
        const col = columns.find(c => c.key === key);
        if (!col?.sortValue) continue;
        const va = col.sortValue(a); const vb = col.sortValue(b);
        if (va == null && vb == null) continue;
        if (va == null) return 1; if (vb == null) return -1;
        const cmp = va < vb ? -1 : va > vb ? 1 : 0;
        if (cmp !== 0) return dir === 'asc' ? cmp : -cmp;
      }
      return 0;
    });
  }, [rows, columns, sorts]);

  // Row selection
  const allSelected = selectable && selectedKeys && sortedRows.length > 0 && sortedRows.every(r => selectedKeys.has(rowKey(r)));

  const toggleAll = useCallback(() => {
    if (!onSelectionChange) return;
    if (allSelected) { onSelectionChange(new Set()); }
    else { onSelectionChange(new Set(sortedRows.map(r => rowKey(r)))); }
  }, [allSelected, sortedRows, rowKey, onSelectionChange]);

  const toggleRow = useCallback((key: string) => {
    if (!onSelectionChange || !selectedKeys) return;
    const next = new Set(selectedKeys);
    if (next.has(key)) next.delete(key); else next.add(key);
    onSelectionChange(next);
  }, [onSelectionChange, selectedKeys]);

  // Column resize
  const handleResizeStart = useCallback((key: string, e: React.MouseEvent) => {
    e.preventDefault(); e.stopPropagation();
    const th = (e.target as HTMLElement).closest('th');
    if (!th) return;
    resizingRef.current = { key, startX: e.clientX, startW: th.offsetWidth };
    const onMove = (me: MouseEvent) => {
      if (!resizingRef.current) return;
      const delta = me.clientX - resizingRef.current.startX;
      setColWidths(prev => ({ ...prev, [key]: Math.max(60, resizingRef.current!.startW + delta) }));
    };
    const onUp = () => { resizingRef.current = null; document.removeEventListener('mousemove', onMove); document.removeEventListener('mouseup', onUp); };
    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
  }, []);

  // Keyboard nav
  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'j' || e.key === 'ArrowDown') { e.preventDefault(); setFocusedRow(prev => Math.min(prev + 1, sortedRows.length - 1)); }
    else if (e.key === 'k' || e.key === 'ArrowUp') { e.preventDefault(); setFocusedRow(prev => Math.max(prev - 1, 0)); }
    else if (e.key === 'Enter' && focusedRow >= 0 && onRowClick) { e.preventDefault(); onRowClick(sortedRows[focusedRow]!); }
    else if (e.key === ' ' && focusedRow >= 0 && selectable) { e.preventDefault(); toggleRow(rowKey(sortedRows[focusedRow]!)); }
  }, [sortedRows, focusedRow, onRowClick, selectable, toggleRow, rowKey]);

  if (rows.length === 0) {
    return (
      <div style={{ padding: 40, textAlign: 'center', background: 'var(--bg-surface)', borderRadius: 'var(--radius-lg)', border: '1px solid var(--border-subtle)', fontSize: 13, color: 'var(--text-ghost)' }}>
        {filteredEmpty ? <><div style={{ fontSize: 20, marginBottom: 8 }}>⊘</div><div>No rows match current filters</div>{totalRows != null && <div style={{ fontSize: 11, color: 'var(--text-dim)', marginTop: 4 }}>{totalRows} total before filtering</div>}</> : <><div style={{ fontSize: 20, marginBottom: 8 }}>∅</div><div>{emptyMessage}</div></>}
      </div>
    );
  }

  return (
    <div style={{ background: 'var(--bg-surface)', borderRadius: 'var(--radius-lg)', border: '1px solid var(--border-subtle)', overflowX: 'auto' }} tabIndex={0} onKeyDown={handleKeyDown}>
      <table className={stickyFirstCol ? 'sticky-col-first' : ''} style={{ width: '100%', borderCollapse: 'collapse', tableLayout: 'fixed' }}>
        <colgroup>
          {selectable && <col style={{ width: '36px' }} />}
          {columns.map(c => <col key={c.key} style={{ width: colWidths[c.key] ? `${colWidths[c.key]}px` : c.width }} />)}
        </colgroup>
        <thead>
          <tr style={{ background: 'var(--bg-raised)' }}>
            {selectable && (
              <th style={{ padding: '10px 8px', borderBottom: '1px solid var(--border-subtle)', textAlign: 'center' }}>
                <input type="checkbox" checked={allSelected} onChange={toggleAll} style={{ cursor: 'pointer' }} />
              </th>
            )}
            {columns.map(col => {
              const sortable = !!col.sortValue;
              const sortIdx = sorts.findIndex(s => s.key === col.key);
              const active = sortIdx >= 0;
              const tip = col.tip || METRIC_TIPS[col.key];
              return (
                <th key={col.key}
                  onClick={sortable ? (e) => handleSort(col.key, e.shiftKey) : undefined}
                  data-tip={tip || undefined}
                  style={{ padding: '10px 16px', fontSize: 10, fontWeight: 700, color: active ? 'var(--text-secondary)' : 'var(--text-ghost)', letterSpacing: '0.07em', textTransform: 'uppercase', textAlign: col.align || 'left', cursor: sortable ? 'pointer' : 'default', userSelect: 'none', borderBottom: '1px solid var(--border-subtle)', whiteSpace: 'nowrap', position: 'relative' }}>
                  {col.label}
                  {tip && <span style={{ fontSize: 8, marginLeft: 3, opacity: 0.5 }}>ⓘ</span>}
                  {sortable && (
                    <span style={{ fontSize: 8, marginLeft: 4, opacity: active ? 1 : 0.3 }}>
                      {active ? (sorts[sortIdx]!.dir === 'asc' ? '▲' : '▼') : '⇅'}
                      {active && sorts.length > 1 && <sup style={{ fontSize: 7 }}>{sortIdx + 1}</sup>}
                    </span>
                  )}
                  {/* Resize handle */}
                  {col.resizable !== false && (
                    <span onMouseDown={(e) => handleResizeStart(col.key, e)}
                      style={{ position: 'absolute', right: 0, top: 0, bottom: 0, width: 4, cursor: 'col-resize', background: 'transparent' }}
                      onMouseEnter={e => (e.currentTarget.style.background = 'var(--blue)')}
                      onMouseLeave={e => (e.currentTarget.style.background = 'transparent')} />
                  )}
                </th>
              );
            })}
          </tr>
        </thead>
        <tbody>
          {sortedRows.map((row, i) => {
            const key = rowKey(row);
            const selected = selectedKeys?.has(key);
            return (
              <tr key={key} data-hoverable=""
                onClick={onRowClick ? () => onRowClick(row) : undefined}
                style={{ cursor: onRowClick ? 'pointer' : 'default', transition: 'background var(--ease-fast)', borderBottom: '1px solid var(--border-faint)', outline: i === focusedRow ? '2px solid var(--blue)' : 'none', outlineOffset: -2, background: selected ? 'var(--blue-bg)' : undefined }}>
                {selectable && (
                  <td style={{ padding: '11px 8px', textAlign: 'center' }}>
                    <input type="checkbox" checked={!!selected} onChange={() => toggleRow(key)} onClick={e => e.stopPropagation()} style={{ cursor: 'pointer' }} />
                  </td>
                )}
                {columns.map(col => (
                  <td key={col.key} style={{ padding: '11px 16px', textAlign: col.align || 'left', verticalAlign: 'middle' }}>{col.render(row)}</td>
                ))}
              </tr>
            );
          })}
        </tbody>
      </table>
      <div style={{ padding: '6px 16px', fontSize: 10, color: 'var(--text-dim)', borderTop: '1px solid var(--border-faint)', display: 'flex', justifyContent: 'space-between' }}>
        <span>
          {selectedKeys && selectedKeys.size > 0 && <span style={{ color: 'var(--blue)', marginRight: 8 }}>{selectedKeys.size} selected</span>}
          {sortedRows.length} row{sortedRows.length !== 1 ? 's' : ''}{totalRows != null && totalRows !== sortedRows.length ? ` of ${totalRows}` : ''}
          {sorts.length > 1 && <span style={{ marginLeft: 8, color: 'var(--text-ghost)' }}>Shift+click for multi-sort</span>}
        </span>
        <span style={{ color: 'var(--text-dead)' }}>j/k nav · Enter open{selectable ? ' · Space select' : ''}</span>
      </div>
    </div>
  );
}
